"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Volume2Icon,
  VolumeXIcon,
  PlayIcon,
  PauseIcon,
} from "@/components/ui/icons";

interface TextToSpeechPlayerProps {
  text?: string;
  enabled?: boolean;
  audioUrl?: string;
  externalAudioRef?: React.RefObject<HTMLAudioElement | null>;
  audioRef?: React.RefObject<HTMLAudioElement | null>;
  voice?: string;
  speed?: number;
  playbackRate?: number;
  size?: "sm" | "md" | "lg";
  mode?: "compact" | "full";
  className?: string;
  onPlay?: () => void;
  onPause?: () => void;
  onEnd?: () => void;
  /** True when user is in a first-attempt student session; disables forward seek */
  blockForwardSeek?: boolean;
  /** True when rendering in preview; seeking should be allowed */
  isPreview?: boolean;
  /** True when user has already completed/listened; seeking should be allowed */
  alreadyCompleted?: boolean;
  /** Whether to show the play/pause button; hidden by default */
  showPlayButton?: boolean;
  /** Optional countdown before audio starts playing */
  playDelaySeconds?: number;
  /** Whether the listener may pause or cancel an in-progress countdown */
  allowPause?: boolean;
  /** Whether the listener may seek backward or replay after finishing */
  allowRewind?: boolean;
}

export default function TextToSpeechPlayer({
  // text = "", // Reserved for future TTS implementation
  enabled = false,
  audioUrl,
  externalAudioRef,
  audioRef: audioRefProp,
  voice = "Default",
  // speed = 1.0, // Reserved for future playback speed control
  playbackRate = 1,
  size = "md",
  mode = "full",
  className = "",
  onPlay,
  onPause,
  onEnd,
  blockForwardSeek = false,
  isPreview = false,
  alreadyCompleted = false,
  showPlayButton = false,
  playDelaySeconds = 0,
  allowPause = true,
  allowRewind = true,
}: TextToSpeechPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [maxListenedTime, setMaxListenedTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [countdownRemaining, setCountdownRemaining] = useState<number | null>(
    null,
  );
  const [hasPlaybackEnded, setHasPlaybackEnded] = useState(false);
  const internalAudioRef = useRef<HTMLAudioElement>(null);
  const resolvedAudioRef = audioRefProp ?? internalAudioRef;
  const usesExternalAudio = !!externalAudioRef;
  const countdownIntervalRef = useRef<number | null>(null);
  const delayedPlayTimeoutRef = useRef<number | null>(null);

  const hasAnyAudio = !!audioUrl || !!externalAudioRef;

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24,
  };

  const getAudioEl = useCallback(
    () => externalAudioRef?.current ?? resolvedAudioRef.current,
    [externalAudioRef, resolvedAudioRef]
  );

  const clearDelayedPlay = useCallback(() => {
    if (countdownIntervalRef.current !== null) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    if (delayedPlayTimeoutRef.current !== null) {
      window.clearTimeout(delayedPlayTimeoutRef.current);
      delayedPlayTimeoutRef.current = null;
    }

    setCountdownRemaining(null);
  }, []);

  useEffect(() => {
    if (!enabled || !hasAnyAudio) {
      return;
    }

    let pollInterval: NodeJS.Timeout | null = null;
    let currentAudioEl: HTMLAudioElement | null = null;
    let cleanupFn: (() => void) | null = null;

    const setupListeners = (audio: HTMLAudioElement) => {
      currentAudioEl = audio;

      if (externalAudioRef?.current && resolvedAudioRef.current && resolvedAudioRef.current !== externalAudioRef.current) {
        resolvedAudioRef.current.pause();
      }

      const handlePlay = () => {
        setIsPlaying(true);
        setHasPlaybackEnded(false);
        onPlay?.();
      };
      const handlePause = () => {
        setIsPlaying(false);
        onPause?.();
      };
      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(allowRewind ? 0 : audio.duration || 0);
        setHasPlaybackEnded(true);
        onEnd?.();
      };
      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
        setMaxListenedTime((prev) => Math.max(prev, audio.currentTime));
      };
      const handleLoadedMetadata = () => setDuration(audio.duration || 0);

      audio.addEventListener("play", handlePlay);
      audio.addEventListener("pause", handlePause);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);

      // Initial sync - ensure we capture the current state
      const syncState = () => {
        setIsPlaying(!audio.paused);
        setCurrentTime(audio.currentTime);
        setDuration(audio.duration || 0);
      };
      
      // Sync immediately
      syncState();
      
      // Also sync after a short delay to handle any async loading
      setTimeout(syncState, 50);

      return () => {
        audio.removeEventListener("play", handlePlay);
        audio.removeEventListener("pause", handlePause);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
    };

    const checkAndBind = () => {
      const nextAudio = getAudioEl();
      if (!nextAudio) return false;
      if (currentAudioEl === nextAudio) return true;
      if (cleanupFn) cleanupFn();
      cleanupFn = setupListeners(nextAudio);
      return true;
    };

    if (!checkAndBind()) {
      pollInterval = setInterval(() => {
        if (checkAndBind() && pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      }, 50);
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (cleanupFn) cleanupFn();
    };
  }, [allowRewind, enabled, hasAnyAudio, externalAudioRef, resolvedAudioRef, getAudioEl, onEnd, onPause, onPlay]);

  useEffect(() => {
    const audioEl = getAudioEl();
    if (!enabled || !audioEl) {
      return;
    }

    audioEl.playbackRate = playbackRate;
  }, [enabled, getAudioEl, playbackRate]);

  // Reset state when audioUrl changes
  useEffect(() => {
    if (audioUrl) {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setMaxListenedTime(0);
      setHasPlaybackEnded(false);
      clearDelayedPlay();
    }
  }, [audioUrl, clearDelayedPlay]);

  // Force re-binding when audioUrl changes and we're using internal audio
  useEffect(() => {
    if (enabled && audioUrl && !usesExternalAudio && resolvedAudioRef.current) {
      // Load the new audio source
      resolvedAudioRef.current.load();
      resolvedAudioRef.current.playbackRate = playbackRate;
      
      // Sync state immediately after loading
      const audio = resolvedAudioRef.current;
      setIsPlaying(!audio.paused);
      setCurrentTime(audio.currentTime);
      setDuration(audio.duration || 0);
      setHasPlaybackEnded(false);
    }
  }, [audioUrl, enabled, playbackRate, usesExternalAudio, resolvedAudioRef]);

  // Periodic state sync for external audio refs
  useEffect(() => {
    if (!enabled || !usesExternalAudio || !externalAudioRef?.current) {
      return;
    }

    const syncInterval = setInterval(() => {
      const audio = externalAudioRef.current;
      if (audio) {
        setIsPlaying(!audio.paused);
        setCurrentTime(audio.currentTime);
        setDuration(audio.duration || 0);
      }
    }, 100); // Sync every 100ms

    return () => clearInterval(syncInterval);
  }, [enabled, usesExternalAudio, externalAudioRef]);

  useEffect(() => () => clearDelayedPlay(), [clearDelayedPlay]);

  // If TTS is not enabled, don't render anything
  if (!enabled) {
    return null;
  }

  // Only render when we actually have audio to play
  if (!hasAnyAudio) {
    return null;
  }

  // Handle play/pause
  const togglePlay = async () => {
    const audioEl = getAudioEl();
    if (!audioEl) return;

    if (countdownRemaining !== null) {
      if (!allowPause) {
        return;
      }

      clearDelayedPlay();
      return;
    }

    if (isPlaying) {
      if (!allowPause) {
        return;
      }

      audioEl.pause();
    } else {
      if (hasPlaybackEnded && !allowRewind) {
        return;
      }

      if (playDelaySeconds > 0) {
        const delaySeconds = Math.max(Math.floor(playDelaySeconds), 0);

        if (delaySeconds > 0) {
          setCountdownRemaining(delaySeconds);
          countdownIntervalRef.current = window.setInterval(() => {
            setCountdownRemaining((current) => {
              if (current === null || current <= 1) {
                return current;
              }

              return current - 1;
            });
          }, 1000);
          delayedPlayTimeoutRef.current = window.setTimeout(async () => {
            clearDelayedPlay();

            try {
              await audioEl.play();
            } catch (error) {
              console.error("Error playing audio:", error);
            }
          }, delaySeconds * 1000);
          return;
        }
      }

      try {
        await audioEl.play();
      } catch (error) {
        console.error("Error playing audio:", error);
      }
    }
  };

  // Handle mute/unmute
  const toggleMute = () => {
    const audioEl = getAudioEl();
    if (!audioEl) return;
    audioEl.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  // Handle volume change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    const audioEl = getAudioEl();
    if (audioEl) {
      audioEl.volume = newVolume;
    }
  };

  // Handle seek
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    const audioEl = getAudioEl();
    if (!audioEl) return;

    const seekingForward = newTime > audioEl.currentTime + 0.1;
    const seekingBackward = newTime < audioEl.currentTime - 0.1;
    const forwardLimit = Math.max(maxListenedTime, audioEl.currentTime);
    const beyondListened = newTime > forwardLimit + 0.25;
    const seekBlocked =
      blockForwardSeek &&
      !isPreview &&
      !alreadyCompleted &&
      seekingForward &&
      beyondListened;
    const rewindBlocked = !allowRewind && seekingBackward;

    if (seekBlocked || rewindBlocked) {
      const clamped = seekBlocked
        ? Math.min(newTime, forwardLimit)
        : audioEl.currentTime;
      setCurrentTime(clamped);
      audioEl.currentTime = clamped;
      return;
    }

    setHasPlaybackEnded(false);
    setCurrentTime(newTime);
    audioEl.currentTime = newTime;
  };

  // Format time (seconds to mm:ss)
  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const playPauseDisabled =
    (countdownRemaining !== null || isPlaying || hasPlaybackEnded) &&
    ((!allowPause && !hasPlaybackEnded) || (!allowRewind && hasPlaybackEnded));
  const playPauseTitle =
    countdownRemaining !== null
      ? allowPause
        ? "Cancel countdown"
        : "Audio starting"
      : isPlaying
        ? allowPause
          ? "Pause"
          : "Pause disabled"
        : hasPlaybackEnded && !allowRewind
          ? "Replay disabled"
          : "Play";

  // Compact mode - just a play button with indicator
  if (mode === "compact") {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        {showPlayButton && (
          <button
            onClick={togglePlay}
            disabled={playPauseDisabled}
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-all shadow-lg hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-70 ${
              countdownRemaining !== null
                ? "bg-amber-500 text-white hover:bg-amber-500"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
            title={playPauseTitle}
          >
            {countdownRemaining !== null ? (
              <span className="text-sm font-semibold">{countdownRemaining}</span>
            ) : isPlaying ? (
              <PauseIcon size={iconSizes[size]} color="white" />
            ) : (
              <PlayIcon size={iconSizes[size]} color="white" />
            )}
          </button>
        )}

        <div className="flex-1">
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${
                duration > 0 ? (currentTime / duration) * 100 : 0
              }%, #d1d5db ${
                duration > 0 ? (currentTime / duration) * 100 : 0
              }%, #d1d5db 100%)`,
            }}
          />
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
          {countdownRemaining !== null && (
            <p className="mt-2 text-xs font-medium text-amber-700">
              Get ready. Audio starts in {countdownRemaining}s.
            </p>
          )}
        </div>

        {/* Audio element */}
        {!usesExternalAudio && audioUrl && (
          <audio 
            key={audioUrl} // Force re-creation when URL changes
            ref={resolvedAudioRef} 
            src={audioUrl} 
          />
        )}
      </div>
    );
  }

  // Full mode - complete audio player
  return (
    <div
      className={`bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-200 ${className}`}
    >
      <div className="flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <Volume2Icon size={16} color="white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Text-to-Speech Audio
              </p>
              <p className="text-xs text-gray-600">
                {`Voice: ${voice}`}
              </p>
            </div>
          </div>

          {/* Mute button */}
          {audioUrl && (
            <button
              onClick={toggleMute}
              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? (
                <VolumeXIcon size={iconSizes[size]} className="text-gray-600" />
              ) : (
                <Volume2Icon size={iconSizes[size]} className="text-gray-600" />
              )}
            </button>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          {/* Play/Pause button */}
          {showPlayButton && (
            <button
              onClick={togglePlay}
              disabled={!audioUrl || playPauseDisabled}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                !audioUrl || playPauseDisabled
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : countdownRemaining !== null
                  ? "bg-amber-500 text-white hover:bg-amber-500 shadow-lg hover:shadow-xl"
                  : "bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl"
              }`}
              title={
                !audioUrl
                  ? "Audio not available yet"
                  : playPauseTitle
              }
            >
              {countdownRemaining !== null ? (
                <span className="text-sm font-semibold">{countdownRemaining}</span>
              ) : isPlaying ? (
                <PauseIcon size={iconSizes[size]} color="white" />
              ) : (
                <PlayIcon size={iconSizes[size]} color="white" />
              )}
            </button>
          )}

          {/* Progress bar */}
          <div className="flex-1">
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              disabled={!audioUrl}
              className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                background: audioUrl
                  ? `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${
                      (currentTime / duration) * 100
                    }%, #d1d5db ${
                      (currentTime / duration) * 100
                    }%, #d1d5db 100%)`
                  : "#d1d5db",
              }}
            />
            <div className="flex justify-between text-xs text-gray-600 mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
            {countdownRemaining !== null && (
              <p className="mt-2 text-xs font-medium text-amber-700">
                Get ready. Audio starts in {countdownRemaining}s.
              </p>
            )}
          </div>

          {/* Volume control */}
          {audioUrl && (
            <div className="hidden md:flex items-center gap-2 w-24">
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          )}
        </div>

        {/* Audio element */}
        {!usesExternalAudio && audioUrl && (
          <audio 
            key={audioUrl} // Force re-creation when URL changes
            ref={resolvedAudioRef} 
            src={audioUrl} 
          />
        )}
      </div>
    </div>
  );
}
